import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useProgressData } from './useProgressData';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const w1w2 = payload.find(p => p.dataKey === 'w1w2')?.value || 0;
  const w3 = payload.find(p => p.dataKey === 'w3')?.value || 0;
  const total = w1w2 + w3;
  if (total === 0) return null;
  return (
    <div className="bg-white text-gray-800 text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-200">
      <div className="font-semibold mb-1">{label}</div>
      {w1w2 > 0 && <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: '#006837' }} />
        W1/W2 Pooled: {w1w2}
      </div>}
      {w3 > 0 && <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: '#F37021' }} />
        W3/Bilateral: {w3}
      </div>}
      <div className="text-gray-500 mt-1 border-t border-gray-200 pt-1">Total: {total}</div>
    </div>
  );
};

export const InnovationPipelineChart = memo(function InnovationPipelineChart() {
  const {
    byInnovationUseLevel, byReadinessLevel,
    byInnovationUseLevelAndFunding, byReadinessLevelAndFunding,
    filteredResults,
  } = useProgressData();

  const hasUse = byInnovationUseLevel && Object.keys(byInnovationUseLevel).length > 0;
  const hasReadiness = byReadinessLevel && Object.keys(byReadinessLevel).length > 0;
  if (!hasUse && !hasReadiness) return (
    <ChartWrapper title="Innovation Pipeline">
      <NoChartData message="No innovation pipeline scores available" detail="This chart shows Innovation Use Level and Readiness Level distributions (scale 1-9). No innovation results with use-level or readiness-level scores were found in the current selection." />
    </ChartWrapper>
  );

  const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  const useData = levels.map(l => {
    const f = byInnovationUseLevelAndFunding?.[l] || { W1W2: 0, W3: 0 };
    return { level: `Level ${l}`, w1w2: f.W1W2, w3: f.W3 };
  });
  const readinessData = levels.map(l => {
    const f = byReadinessLevelAndFunding?.[l] || { W1W2: 0, W3: 0 };
    return { level: `Level ${l}`, w1w2: f.W1W2, w3: f.W3 };
  });

  const useTotalN = Object.values(byInnovationUseLevel || {}).reduce((s, v) => s + v, 0);
  const readinessTotalN = Object.values(byReadinessLevel || {}).reduce((s, v) => s + v, 0);

  const exportData = levels.map(l => {
    const uf = byInnovationUseLevelAndFunding?.[l] || { W1W2: 0, W3: 0 };
    const rf = byReadinessLevelAndFunding?.[l] || { W1W2: 0, W3: 0 };
    return {
      Level: l,
      'Use W1/W2': uf.W1W2,
      'Use W3': uf.W3,
      'Readiness W1/W2': rf.W1W2,
      'Readiness W3': rf.W3,
    };
  });

  const renderUseStackTotal = (props) => {
    const { x, y, width, index } = props;
    const entry = useData[index];
    if (!entry) return null;
    const total = entry.w1w2 + entry.w3;
    if (total === 0) return null;
    return (
      <text x={x + width / 2} y={y - 4} fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} textAnchor="middle">
        {total}
      </text>
    );
  };

  const renderReadinessStackTotal = (props) => {
    const { x, y, width, index } = props;
    const entry = readinessData[index];
    if (!entry) return null;
    const total = entry.w1w2 + entry.w3;
    if (total === 0) return null;
    return (
      <text x={x + width / 2} y={y - 4} fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} textAnchor="middle">
        {total}
      </text>
    );
  };

  const fundingLegend = (
    <div className="flex items-center justify-center gap-4 mt-1 text-xs text-gray-600">
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#006837' }} />
        <span>W1/W2 Pooled</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F37021' }} />
        <span>W3/Bilateral</span>
      </div>
    </div>
  );

  return (
    <ChartWrapper title="Innovation Pipeline" info="Distribution of Innovation Use Levels (1–9) and Readiness Levels (1–9) across all innovation results, split by funding type." chartData={exportData} exportFilename="innovation-pipeline" sourceResults={filteredResults}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {hasUse && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Innovation Use Level (1-9)</h5>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={useData} margin={{ top: 16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="level" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="w1w2" stackId="a" fill="#006837" />
                <Bar dataKey="w3" stackId="a" fill="#F37021" radius={[4, 4, 0, 0]}>
                  <LabelList content={renderUseStackTotal} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 text-center mt-1">Based on {useTotalN} innovation results</p>
          </div>
        )}
        {hasReadiness && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Readiness Level (1-9)</h5>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={readinessData} margin={{ top: 16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="level" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="w1w2" stackId="a" fill="#006837" />
                <Bar dataKey="w3" stackId="a" fill="#F37021" radius={[4, 4, 0, 0]}>
                  <LabelList content={renderReadinessStackTotal} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 text-center mt-1">Based on {readinessTotalN} innovation results</p>
          </div>
        )}
      </div>
      {fundingLegend}
    </ChartWrapper>
  );
});
